import { RJSFSchema } from '@rjsf/utils';
import { FieldMappingsConfig } from 'routes/transformationRules/fieldMappingTypes';

// CUSTOM TYPES
export type UserWorkspace = {
  id: string;
  workspace_id: string;
  name: string;
  role: string;
};

export type UserProfile = {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_superuser: boolean;
  accepted_eula: boolean;
  contact_email: string | null;
  account_setup_complete: boolean;
  workspaces: UserWorkspace[];
};

export type Member = {
  email: string;
  first_name: string;
  full_name: string | null;
  id?: Number | null;
  last_name: string;
  role: string | null;
};

export type Organization = {
  id?: string;
  name: string;
  description: string;
  role?: string;
};

export type Connection = {
  id: string;
  name: string;
  type: ConnectionType;
  owner: Organization;
  base_url: string;
  enabled: boolean;
  configurations?: any;
  status: string;
};

export type Source = {
  id: string;
  external_id: string;
  name?: string;
  // Immutable origin: "manual" was typed in by a user, "ingestion" was discovered
  // when the integration first sent data for it.
  created_via?: 'ingestion' | 'manual';
  update_frequency: string;
  last_update: string;
  routing_rules: RoutingRule[];
  destinations: Connection[];
  status: string;
  provider: Provider;
  created_at: string;
};

export type SourceWithRoutes = {
  source: Source;
  routes: any[];
};

export type NewConnection = {
  id: string;
  name: string;
  type: string;
  owner: string;
  url: string;
  enabled: true;
  configuration: object;
};

export type ConnectionType = {
  id: string;
  name: string;
  description: string;
  value: string;
  actions: IntegrationAction[];
  help_center_url?: string;
};

/** Sentinel value — provider types with this description are hidden from the grid and search results. */
export const HIDE_TECHNOLOGY_CARD_SENTINEL = 'hide-technology-card';

export type ProviderType = {
  id: string;
  name: string;
  description: string;
  value: string;
  actions: IntegrationAction[];
  help_center_url?: string;
  webhook?: IntegrationWebhook;
};

export type IntegrationAction = {
  id: string;
  name: string;
  type: string;
  value: string;
  description: string;
  schema: RJSFSchema;
};

export type IntegrationWebhook = {
  id: string;
  name: string;
  value: string;
  schema?: RJSFSchema;
};

export type WebhookConfiguration = {
  id: string;
  integration: string;
  webhook: IntegrationWebhook;
  data: any;
};

export type Provider = {
  id: string;
  name: string;
  type: ProviderType;
  base_url: string;
  status: string;
  configurations?: any;
  enabled: boolean;
  owner?: Organization;
  webhook_configuration?: WebhookConfiguration;
};

export type RouteConfiguration = {
  id?: string;
  name?: string;
  data?: FieldMappingsConfig;
};

export type RoutingRule = {
  id: string;
  name: string;
  configuration?: RouteConfiguration;
};

export type Route = {
  provider: Provider;
  destinations: Connection[];
  default_route: RoutingRule;
  owner: Organization;
  status: string;
  id: string;
  enabled: boolean;
};

export type NewIntegration = {
  name: string;
  base_url: string;
  enabled: boolean;
  type: string;
  owner: string;
  configurations?: NewConnectionConfiguration[];
  create_default_route: boolean;
};

export type NewConnectionConfiguration = {
  action: string;
  data: any;
};

export type ConnectionConfiguration = {
  action: IntegrationAction;
  action_id?: string;
  data: any;
  id: string;
};

export type RouteLog = {
  id: string;
  created_at: string;
  log_level: number;
  log_type: string;
  origin: string;
  value: string;
  title: string;
  created_by: Member;
  details: any;
  is_reversable: boolean;
  revert_data: any;
};

export const getRouteHealthStatuses = (t: (key: string) => string) => [
  {
    name: t('Healthy'),
    value: 'healthy',
  },
  {
    name: t('Unhealthy'),
    value: 'unhealthy',
  },
  {
    name: t('Needs Review'),
    value: 'needs_review',
  },
  {
    name: t('Disabled'),
    value: 'disabled',
  },
];

export const portalLanguages = (t: (key: string) => string) => [
  {
    label: 'English',
    value: 'en',
  },
  {
    label: 'Español',
    value: 'es',
  },
  {
    label: 'Français',
    value: 'fr',
  },
];

// FACTORY FUNCTIONS
export const buildMember = (
  email: string | null = null,
  first_name: string | null = null,
  full_name: string | null = null,
  last_name: string | null = null,
  role: string | null = null,
  id: Number | null = null,
): Member => {
  const member: Member = {
    email: email ? email : '',
    first_name: first_name ? first_name : '',
    full_name: full_name,
    last_name: last_name ? last_name : '',
    role: role,
    id: id,
  };
  return member;
};

export const buildConnectionType = (
  id: string | null = null,
  name: string | null = null,
  description: string | null = null,
  value: string | null = null,
  actions: IntegrationAction[] | null = null,
): ConnectionType => {
  const destination_type: ConnectionType = {
    id: id ? id : '',
    name: name ? name : '',
    description: description ? description : '',
    value: value ? value : '',
    actions: actions ? actions : [],
  };
  return destination_type;
};

export const buildConnection = (
  id: string | null = null,
  name: string | null = null,
  type: ConnectionType | null = null,
  owner: Organization | null = null,
  base_url: string | null = null,
  enabled: boolean | null = null,
  configurations: any | null = null,
  status: string | null = null,
): Connection => {
  const destination: Connection = {
    id: id ? id : '',
    name: name ? name : '',
    type: type ? type : buildConnectionType(),
    owner: owner ? owner : buildOrganization(),
    base_url: base_url ? base_url : '',
    enabled: enabled ? enabled : true,
    configurations: configurations ? configurations : {},
    status: status ? status : '',
  };
  return destination;
};

export const buildSource = (): Source => {
  const source: Source = {
    id: '',
    external_id: '',
    update_frequency: '',
    last_update: '',
    routing_rules: [],
    destinations: [],
    status: '',
    provider: buildProvider(),
    created_at: '',
  };
  return source;
};

export const buildProviderType = (): ProviderType => {
  const provider_type: ProviderType = {
    id: '',
    name: '',
    description: '',
    value: '',
    actions: [
      {
        id: '',
        name: '',
        type: '',
        value: '',
        description: '',
        schema: {},
      },
    ],
  };
  return provider_type;
};

export const buildNewIntegration = (
  create_default_route: boolean,
): NewIntegration => {
  const new_integration: NewIntegration = {
    name: '',
    base_url: '',
    enabled: true,
    type: '',
    owner: '',
    create_default_route: create_default_route,
  };
  return new_integration;
};

export const buildRoute = (
  provider: Provider | null = null,
  destinations: Connection[] | null = null,
  default_route: RoutingRule | null = null,
  owner: Organization | null = null,
  status: string | null = null,
  id: string | null = null,
  enabled: boolean | null = null,
): Route => {
  const route: Route = {
    provider: provider ? provider : buildProvider(),
    destinations: destinations ? destinations : [],
    default_route: default_route ? default_route : buildRoutingRule(),
    owner: owner ? owner : buildOrganization(),
    status: status ? status : '',
    id: id ? id : '',
    enabled: enabled ? enabled : true,
  };
  return route;
};

export const buildProvider = (
  id: string | null = null,
  name: string | null = null,
  type: ProviderType | null = null,
  base_url: string | null = null,
  status: string | null = null,
  configurations: any | null = null,
  enabled: boolean | null = null,
): Provider => {
  const provider: Provider = {
    id: id ? id : '',
    name: name ? name : '',
    type: type ? type : buildProviderType(),
    base_url: base_url ? base_url : '',
    status: status ? status : '',
    configurations: configurations ? configurations : {},
    enabled: enabled ? enabled : true,
  };
  return provider;
};

export const buildRoutingRule = (): RoutingRule => {
  const routing_rule: RoutingRule = {
    id: '',
    name: '',
  };
  return routing_rule;
};

export const buildOrganization = (
  name: string | null = null,
  description: string | null = null,
  role: string | null = null,
): Organization => {
  const organization: Organization = {
    name: name ? name : '',
    description: description ? description : '',
    role: role ? role : '',
  };
  return organization;
};

// Connection types whose URL field is hidden in the UI — do not add types here
// just because they have special URL handling (e.g. EarthRanger site-name field).
export enum ConnectionTypeValues {
  TrapTagger = 'TrapTagger',
  wpsWatch = 'wpsWatch',
  Movebank = 'Movebank',
}

export const EARTHRANGER_CONNECTION_TYPE = 'EarthRanger';

export const LogTypes = [
  {
    label: 'Changes',
    value: 'cdc',
  },
  {
    label: 'Activity',
    value: 'ev',
  },
];

export const LogLevel = [
  {
    label: 'Debug',
    value: '10',
  },
  {
    label: 'Info',
    value: '20',
  },
  {
    label: 'Warning',
    value: '30',
  },
  {
    label: 'Error',
    value: '40',
  },
];

export const DateRange = [
  {
    label: 'Past hour',
    value: '1h',
  },
  {
    label: 'Past 3 hours',
    value: '3h',
  },
  {
    label: 'Past day',
    value: '1d',
  },
  {
    label: 'Past week',
    value: '7d',
  },
  {
    label: 'Past month',
    value: '30d',
  },
  {
    label: 'Custom',
    value: 'custom',
  },
];
